import Logger from '../lib/logger';
import constants from './constants';
import ObserverIntersection, {
  resolveThreshold,
  addPixelsToThreshold,
  isSmartLazyLoadingEnabled,
} from './observer_intersection';

export default class BidManager {
  /**
   * The BidManager class communicates with our bidding partners, namely two:
   * 1. Prebid.js (which encapsulates many bidding partners)
   * 2. Amazon (which has its own header bidding system, for more money)
   *
   * BidManager accepts instances of the Bid class, and it queues them until they
   * are ready to be fetched. On page load, we wait to define all the slots on the
   * page, queue up their bids, and request them in one batch. As slots refresh
   * further down the page, their bids will be fetched on an individual basis
   * instead of in bulk.
   */

  constructor({ app }) {
    this.app = app;
    this.dependenciesResolved = true;
    this.readyToBeBid = [];
    this._numberOfBidsFetched = 0;
    this.activelyBidding = [];
    this.biddingPartners = [];
    this.beforeFirstBidQueue = [];
    this.fetchTimer = 0;
  }

  get prebidTimeout() {
    return this.app.settings.prebid.auctionTimeout || 2000;
  }

  get shouldLazyBid() {
    return Boolean(this.app.settings.lazyBid || this.app.settings.lazy_bid); // Temporary, to support configs until they are deployed
  }

  /**
   * Add a bid to the queue to be fetched
   * @param {Bid} bid  Bid instance
   */
  addBid(bid) {
    // Auction now when lazy bidding is off, or when the slot is eager-eligible
    // (Slot#isEagerEligible) — an eager slot fetches its bid immediately instead of deferring the
    // auction until it approaches the viewport. Otherwise park the bid on the lazy-bid observer
    // below and auction as the slot nears the viewport.
    if (!this.shouldLazyBid || bid.slot.isEagerEligible()) {
      this.enqueueBid(bid);
      Logger.log(`Lazy loading of bids is not enabled for ${bid.slot.name}.`);
      return;
    }

    Logger.log(
      `Lazy loading of bids is enabled for ${bid.slot.name}, bids will be requested as slot approaches viewport.`
    );

    // Check if slot element exists (prevents errors with adblock)
    if (!bid.slot.element) {
      Logger.log(`Could not find element for ${bid.slot.name}, bid will not be processed.`);
      return;
    }

    // Watch slot for intersection using ObserverIntersection
    // We provide our own callback that handles the business logic (triggering auction)
    let auctionThreshold;

    const settings = this.app.settings || {};
    const useSmartLazyLoading = isSmartLazyLoadingEnabled(settings);

    if (useSmartLazyLoading) {
      // Use smart threshold resolution (section + device + platform)
      // Then add configurable pixels to top and bottom for auction threshold
      const resolvedThreshold = resolveThreshold(bid.slot, bid.slot.element);
      // Parse adjustment value to ensure it's a number (handle string values like "100px" or "100")
      const rawAdjustment =
        settings.smartLazyLoad?.auctionThresholdAdjustment ?? constants.DEFAULT_AUCTION_THRESHOLD_ADJUSTMENT;
      const adjustmentPixels =
        typeof rawAdjustment === 'string'
          ? parseFloat(rawAdjustment.replace(/[^\d.-]/g, '')) || constants.DEFAULT_AUCTION_THRESHOLD_ADJUSTMENT
          : Number(rawAdjustment) || constants.DEFAULT_AUCTION_THRESHOLD_ADJUSTMENT;
      auctionThreshold = addPixelsToThreshold(resolvedThreshold, adjustmentPixels);
      Logger.log(
        `Using smart lazy loading threshold for auction: ${resolvedThreshold} -> ${auctionThreshold} (adjustment: ${adjustmentPixels}px)`
      );
    } else {
      // Legacy: use explicit threshold or fallback
      auctionThreshold = this.app.settings.prebid?.observerThreshold || '300px';
    }

    ObserverIntersection.watchElement(
      bid.slot.element,
      auctionThreshold,
      () => {
        // Callback: trigger auction for this bid
        Logger.log(`Triggering auction for ${bid.slot.name}`);
        this.enqueueBid(bid);
      },
      {
        settings: this.app.settings,
      }
    );
  }

  /**
   * Queue a bid for the next auction. Idempotent: a bid already waiting or in flight is not queued
   * twice. That matters because a parked bid can reach here from two places — the lazy-bid
   * IntersectionObserver in addBid(), and promoteParkedBid() when a slot is force-requested out of
   * band (GamAdSlot#requestOrQueueForBid). The observer only unobserves on intersection, so a
   * promoted slot that later scrolls into view would otherwise enqueue its bid a second time.
   *
   * @param {Bid} bid
   */
  enqueueBid(bid) {
    if (this.isBidInFlightFor(bid.slot)) {
      Logger.log(`Bid for ${bid.slot.name} is already queued or in flight; not enqueueing again.`);
      return;
    }

    this.readyToBeBid.push(bid);

    clearTimeout(this.fetchTimer);

    this.fetchTimer = setTimeout(() => {
      this.fetchBids();
    }, this.app.settings.prebid.coalesceDelay || 50);
  }

  /**
   * Indiciates if we can fetch the bids yet, or if there are still
   * dependencies to resolve before a fetch can happen
   *
   * @return {Boolean}
   */
  canFetchBids() {
    return this.dependenciesResolved;
  }

  /**
   * Listen for the initial group of ads to get defined, so we can make one
   * request in bulk.
   */
  async init() {
    // resolve all dependencies that have been enqeued before the first bid can run
    await Promise.allSettled(
      this.beforeFirstBidQueue.map(dependencyFunction => {
        return dependencyFunction();
      })
    ).then(results => {
      Logger.log(
        `First bid dependencies were settled with the statuses: ${JSON.stringify(results.map(result => result.status))}`
      );
    });

    this.dependenciesResolved = true;
    Logger.log('Calling initial request for bids');
    this.fetchBids();
  }

  /**
   * Add dependencies to be run before the first bid can be returned
   * If passed a function that returns a promise it will prevent bids from
   * fetching until promise is resolved
   * @param {Function} dependencyFunction a function, can return a promise
   */
  beforeFirstBid(dependencyFunction) {
    this.dependenciesResolved = false;
    this.beforeFirstBidQueue.push(dependencyFunction);
  }

  /**
   * Add bidding partner
   * Used within the Prebid, Rubicon Demand Manager, and Amazon plugins
   */
  addBiddingPartner(partner) {
    this.biddingPartners.push(partner);
  }

  /**
   * Fetch bids from bidding partners, and trigger callbacks.
   * This is only called externally during tests.
   * @param  {function} Optional callback method, used during tests.
   * @return {undefined}
   */
  fetchBids(callback = null) {
    if (!this.canFetchBids()) {
      return false;
    }

    if (!this.readyToBeBid.length) {
      if (callback) {
        callback();
      }
      return;
    }

    if (this.activelyBidding.length) {
      Logger.log('Bid Manager defering fetchBids because some bids are currently being handled');
      return;
    }

    this.activelyBidding = this.readyToBeBid;
    this.readyToBeBid = [];
    this._numberOfBidsFetched += this.activelyBidding.length;

    Logger.log('Fetching bids from all bidding partners');

    Promise.all(
      this.biddingPartners.map(partner => {
        return partner.fetchBidsFor({ queueOfBids: this.activelyBidding, timeout: this.prebidTimeout });
      })
    ).then(() => {
      Logger.log('Bids have been fetched for bidding partners');
      this.biddingPartners.forEach(partner => {
        if (typeof partner.addTargeting === 'function') {
          partner.addTargeting({ queueOfBids: this.activelyBidding });
        }
      });

      this.activelyBidding.forEach(bid => bid.slot.bidded());

      // Send a callback for tests
      if (callback) callback();
      this.activelyBidding = [];

      // Fetch bids, if some have been set to ready
      if (this.readyToBeBid.length) {
        Logger.log('Bid Manager done fetching, but going to run again because some are in the queue');
        this.fetchBids();
      }
    });
  }

  /**
   * Whether this slot's bid is enqueued or currently in flight — i.e. its auction will run and
   * fire bidded() on its own. Returns false when the bid was parked behind the lazy-bid
   * IntersectionObserver in addBid() (shouldLazyBid + watcherEligible) and its viewport trigger
   * has not fired yet: that observer may never fire (e.g. an invisible out-of-page div), so
   * anything waiting on bidded() would hang. Callers use this to decide whether it is safe to
   * defer a render until after bidding, vs. request now.
   *
   * @param  {Slot}     slot
   * @return {boolean}  true if the slot has a bid in readyToBeBid or activelyBidding
   */
  isBidInFlightFor(slot) {
    return this.readyToBeBid.some(bid => bid.slot === slot) || this.activelyBidding.some(bid => bid.slot === slot);
  }

  /**
   * Run the auction now for a slot whose bid is still parked behind the lazy-bid
   * IntersectionObserver. Used when a slot is requested out of band — e.g. `Slot#display()` on a
   * `deferDisplay` interstitial, whose element may never intersect, so the observer that would
   * normally trigger this auction cannot be relied on. Promoting the bid lets the slot render with
   * Prebid targeting instead of firing unbidded.
   *
   * Once enqueued the auction is bounded by `prebidTimeout`, so bidded() is guaranteed to come and
   * it is safe for the caller to wait on it (unlike waiting on a still-parked bid, which may hang
   * forever).
   *
   * @param  {Slot}     slot
   * @return {boolean}  true if a parked bid was queued for auction; false if there is nothing to
   *   promote (no bid, or it is already enqueued / in flight).
   */
  promoteParkedBid(slot) {
    if (!slot || !slot.bid || this.isBidInFlightFor(slot)) return false;

    Logger.log(`Promoting parked bid for ${slot.name}; auctioning now rather than waiting on its observer.`);
    this.enqueueBid(slot.bid);
    return true;
  }

  /**
   * Returns the number of bids waiting to be fetched
   * @return {Number} bids waiting to be fetched
   */
  numberofBidsReadyToBeBid() {
    return this.readyToBeBid.length;
  }

  /**
   * Returns the total count of bids fetched this session
   * useful for debugging and testing.
   * @return {Number} the number of bids fetched this session.
   */
  numberOfBidsFetched() {
    return this._numberOfBidsFetched;
  }

  /**
   * Disable bid manager while slots are re-added.
   */
  disable() {
    this.dependenciesResolved = false;
  }
}
